import {createStore, createEvent, createEffect, sample} from 'effector';
import {IdeasState, IdeasApiResponse, IdeasFilters, Idea, IdeaInvitationsResponse} from '../model/types';
import {ideasService} from '../service/api';
import {showToast} from '@/core/store/app';

// Events
export const fetchIdeasRequested = createEvent<{filters?: IdeasFilters; type?: string | null}>();
export const fetchMyIdeasRequested = createEvent<IdeasFilters | undefined>();
export const fetchMyIdeasPublishedRequested = createEvent<IdeasFilters | undefined>();
export const fetchMyIdeasDraftsRequested = createEvent<IdeasFilters | undefined>();
export const fetchMyIdeasArchivedRequested = createEvent<IdeasFilters | undefined>();
export const fetchIdeaInvitationsRequestsRequested = createEvent<void>();
export const loadMoreIdeasRequested = createEvent<IdeasFilters | undefined>();
export const fetchIdeaDetailRequested = createEvent<string>();
export const updateIdeaBookmark = createEvent<{ideaId: string; isBookmarked: boolean}>();

// Effects
export const fetchIdeasFx = createEffect(
  async ({filters, type}: {filters?: IdeasFilters; type?: string | null}): Promise<IdeasApiResponse> => {
    return await ideasService.getAllIdeas(filters, type);
  },
);

export const fetchMyIdeasFx = createEffect(
  async (
    filters?: IdeasFilters,
  ): Promise<{published: IdeasApiResponse; draft: IdeasApiResponse; archived: IdeasApiResponse}> => {
    const [published, draft, archived] = await Promise.all([
      ideasService.getAllIdeas(filters, 'my-ideas-published'),
      ideasService.getAllIdeas(filters, 'my-ideas-draft'),
      ideasService.getAllIdeas(filters, 'my-ideas-archived'),
    ]);
    return {published, draft, archived};
  },
);

export const fetchMyIdeasPublishedFx = createEffect(async (filters?: IdeasFilters): Promise<IdeasApiResponse> => {
  return await ideasService.getAllIdeas(filters, 'my-ideas-published');
});

export const fetchMyIdeasDraftsFx = createEffect(async (filters?: IdeasFilters): Promise<IdeasApiResponse> => {
  return await ideasService.getAllIdeas(filters, 'my-ideas-draft');
});

export const fetchMyIdeasArchivedFx = createEffect(async (filters?: IdeasFilters): Promise<IdeasApiResponse> => {
  return await ideasService.getAllIdeas(filters, 'my-ideas-archived');
});

export const loadMoreIdeasFx = createEffect(async (filters?: IdeasFilters): Promise<IdeasApiResponse> => {
  return await ideasService.getAllIdeas(filters);
});

export const fetchIdeaDetailFx = createEffect(async (id: string): Promise<Idea> => {
  return await ideasService.getIdeaById(id);
});

export const fetchIdeaInvitationsRequestsFx = createEffect(async (): Promise<IdeaInvitationsResponse> => {
  return await ideasService.getIdeaInvitationsRequests();
});

export const toggleBookmarkFx = createEffect(
  async ({ideaId, isBookmarked}: {ideaId: string; isBookmarked: boolean}): Promise<void> => {
    return await ideasService.toggleBookmark(ideaId, isBookmarked);
  },
);

// Store
export const $ideasStore = createStore<IdeasState>({
  ideas: [],
  ideasPublished: [],
  ideasDrafts: [],
  ideasArchived: [],
  selectedIdea: null,
  invitations: null,
  loading: false,
  loadMore: false,
  error: null,
  filters: {
    search: '',
    sort: 'created',
  },
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    total_count: 0,
  },
  allIdeasCount: 0,
  myIdeasCount: 0,
  totalIdiasCount: 0,
});

// Store handlers
$ideasStore
  .on(fetchIdeasRequested, (state) => ({
    ...state,
    loading: true,
    error: null,
  }))
  .on(fetchMyIdeasRequested, (state) => ({
    ...state,
    loading: true,
    error: null,
  }))
  .on(fetchIdeaInvitationsRequestsRequested, (state) => ({
    ...state,
    loading: true,
    error: null,
  }))
  .on(fetchIdeasFx.doneData, (state, response) => ({
    ...state,
    ideas: response.data,
    loading: false,
    error: null,
    allIdeasCount: response.total_count,
    totalIdiasCount: response.count,
    pagination: {
      ...state.pagination,
      total: response.count,
      total_count: response.total_count,
      page: 1,
    },
  }))
  .on(fetchMyIdeasFx.doneData, (state, {published, draft, archived}) => {
    // console.log('My Ideas API Response:');
    // console.log('Published:', published.data);
    // console.log('Draft:', draft.data);
    // console.log('Archived:', archived.data);

    const myIdeasTotalCount = published.total_count + draft.total_count + archived.total_count;

    return {
      ...state,
      ideasPublished: published.data,
      ideasDrafts: draft.data,
      ideasArchived: archived.data,
      loading: false,
      error: null,
      myIdeasCount: myIdeasTotalCount,
      pagination: {
        ...state.pagination,
        total: myIdeasTotalCount,
        total_count: myIdeasTotalCount,
        page: 1,
      },
    };
  })
  .on(fetchMyIdeasPublishedFx.doneData, (state, response) => ({
    ...state,
    ideasPublished: response.data,
    loading: false,
    error: null,
  }))
  .on(fetchMyIdeasDraftsFx.doneData, (state, response) => ({
    ...state,
    ideasDrafts: response.data,
    loading: false,
    error: null,
  }))
  .on(fetchMyIdeasArchivedFx.doneData, (state, response) => ({
    ...state,
    ideasArchived: response.data,
    loading: false,
    error: null,
  }))
  .on(fetchMyIdeasPublishedFx.failData, (state, error) => {
    showToast({
      type: 'error',
      text: error?.message || 'Failed to load published ideas',
    });
    return {
      ...state,
      loading: false,
      error: error.message,
    };
  })
  .on(fetchMyIdeasDraftsFx.failData, (state, error) => {
    showToast({
      type: 'error',
      text: error?.message || 'Failed to load draft ideas',
    });
    return {
      ...state,
      loading: false,
      error: error.message,
    };
  })
  .on(fetchMyIdeasArchivedFx.failData, (state, error) => {
    showToast({
      type: 'error',
      text: error?.message || 'Failed to load archived ideas',
    });
    return {
      ...state,
      loading: false,
      error: error.message,
    };
  })
  .on(fetchIdeasFx.failData, (state, error) => {
    showToast({
      type: 'error',
      text: error?.message || 'Failed to load ideas',
    });
    return {
      ...state,
      loading: false,
      error: error.message,
    };
  })
  .on(fetchMyIdeasFx.failData, (state, error) => {
    showToast({
      type: 'error',
      text: error?.message || 'Failed to load my ideas',
    });
    return {
      ...state,
      loading: false,
      error: error.message,
    };
  })
  .on(fetchIdeaInvitationsRequestsFx.doneData, (state, invitations) => ({
    ...state,
    invitations: invitations,
    loading: false,
    error: null,
  }))
  .on(fetchIdeaInvitationsRequestsFx.failData, (state, error) => {
    showToast({
      type: 'error',
      text: error?.message || 'Failed to load invitations and requests',
    });
    return {
      ...state,
      loading: false,
      error: error.message,
    };
  })
  .on(loadMoreIdeasRequested, (state) => ({
    ...state,
    loadMore: true,
    error: null,
  }))
  .on(loadMoreIdeasFx.doneData, (state, response) => ({
    ...state,
    ideas: [...state.ideas, ...response.data],
    loadMore: false,
    error: null,
    pagination: {
      ...state.pagination,
      page: state.pagination.page + 1,
      total: response.count,
      total_count: response.total_count,
    },
  }))
  .on(loadMoreIdeasFx.failData, (state, error) => {
    showToast({
      type: 'error',
      text: error?.message || 'Failed to load more ideas',
    });
    return {
      ...state,
      loadMore: false,
      error: error.message,
    };
  })
  .on(updateIdeaBookmark, (state, {ideaId, isBookmarked}) => ({
    ...state,
    ideas: state.ideas.map((idea) => (idea.id === ideaId ? {...idea, is_call_bookmarked: isBookmarked} : idea)),
    selectedIdea:
      state.selectedIdea?.id === ideaId
        ? {...state.selectedIdea, is_call_bookmarked: isBookmarked}
        : state.selectedIdea,
  }))
  .on(fetchIdeaDetailRequested, (state) => ({
    ...state,
    loading: true,
    error: null,
  }))
  .on(fetchIdeaDetailFx.doneData, (state, idea) => ({
    ...state,
    selectedIdea: idea,
    loading: false,
    error: null,
  }))
  .on(fetchIdeaDetailFx.failData, (state, error) => {
    showToast({
      type: 'error',
      text: error?.message || 'Failed to load idea details',
    });
    return {
      ...state,
      loading: false,
      error: error.message,
    };
  });

// Samples
sample({
  clock: fetchIdeasRequested,
  target: fetchIdeasFx,
});

sample({
  clock: fetchMyIdeasRequested,
  target: fetchMyIdeasFx,
});

sample({
  clock: fetchMyIdeasPublishedRequested,
  target: fetchMyIdeasPublishedFx,
});

sample({
  clock: fetchMyIdeasDraftsRequested,
  target: fetchMyIdeasDraftsFx,
});

sample({
  clock: fetchMyIdeasArchivedRequested,
  target: fetchMyIdeasArchivedFx,
});

sample({
  clock: loadMoreIdeasRequested,
  source: $ideasStore,
  fn: (state, filters) => ({
    ...filters,
    page: state.pagination.page + 1,
  }),
  target: loadMoreIdeasFx,
});

sample({
  clock: fetchIdeaDetailRequested,
  target: fetchIdeaDetailFx,
});

sample({
  clock: updateIdeaBookmark,
  target: toggleBookmarkFx,
});

// Add error handling for bookmark toggle
toggleBookmarkFx.failData.watch((error) => {
  showToast({
    type: 'error',
    text: error?.message || 'Failed to update bookmark',
  });
});

sample({
  clock: fetchIdeaInvitationsRequestsRequested,
  target: fetchIdeaInvitationsRequestsFx,
});
